import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { saleOrderService } from "services";
import {
  SaleOrderForm,
  SaleOrderQuery,
  SaleOrderUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type SaleOrderListKey = ["sale-orders"] | ["sale-orders", SaleOrderQuery];
type SaleOrderDetailKey = ["sale-orders", "detail", number | string];

const saleOrderKeys = {
  all: ["sale-orders"] as const,
  list: (params?: SaleOrderQuery): SaleOrderListKey =>
    params ? ["sale-orders", params] : ["sale-orders"],
  detail: (id: number | string): SaleOrderDetailKey => [
    "sale-orders",
    "detail",
    id,
  ],
};

export function useSaleOrder() {
  const queryClient = useQueryClient();

  const ensureSuccess = (res: any, defaultMessage: string) => {
    if (res && (res.statusCode === 200 || res.statusCode === 201)) {
      return res;
    }
    const message =
      res?.message ||
      (typeof res === "string" ? res : null) ||
      defaultMessage ||
      "Có lỗi xảy ra";
    throw new Error(message);
  };

  const fetchList = (params?: SaleOrderQuery, enabled = true) => {
    return useQuery({
      queryKey: saleOrderKeys.list(params),
      queryFn: () => saleOrderService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: saleOrderKeys.detail(id),
      queryFn: () => saleOrderService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createSaleOrder = useMutation({
    mutationFn: async (payload: SaleOrderForm) => {
      const res = await saleOrderService.store(payload);
      return ensureSuccess(res, "Tạo đơn bán hàng thất bại");
    },
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Tạo đơn bán hàng thành công");
      queryClient.invalidateQueries({ queryKey: saleOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error(
        (err as any)?.message ?? "Tạo đơn bán hàng thất bại",
      );
      console.error("createSaleOrder failed", err);
    },
  });

  const updateSaleOrder = useMutation({
    mutationFn: async (payload: SaleOrderUpdatePayload) => {
      const res = await saleOrderService.update(payload);
      return ensureSuccess(res, "Cập nhật đơn bán hàng thất bại");
    },
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Cập nhật đơn bán hàng thành công");
      queryClient.invalidateQueries({ queryKey: saleOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error(
        (err as any)?.message ?? "Cập nhật đơn bán hàng thất bại",
      );
      console.error("updateSaleOrder failed", err);
    },
  });

  const deleteSaleOrder = useMutation({
    mutationFn: async (id: number | string) => {
      const res = await saleOrderService.destroy(id);
      return ensureSuccess(res, "Xoá đơn bán hàng thất bại");
    },
    onSuccess: (res) => {
      SnackbarUtils.success(res?.message ?? "Xoá đơn bán hàng thành công");
      queryClient.invalidateQueries({ queryKey: saleOrderKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error((err as any)?.message ?? "Xoá đơn bán hàng thất bại");
      console.error("deleteSaleOrder failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createSaleOrder,
    updateSaleOrder,
    deleteSaleOrder,
  };
}
